/**
 * FmTopBar — glass 52px top bar (DS-reconciled: 40a2l.14)
 * DEFECT 1 fix: nav absolutely centered
 * DEFECT 2 fix: translucent `--fm-glass-bar` surface with blur
 * DEFECT 3 fix: __in inner wrapper max-width:var(--fm-shell-max)
 *
 * T2: FmTopBar shell
 *
 * GOVERNED NAV (muem9.4): the primary nav is driven SOLELY by the typed route
 * catalog (`fmRouteCatalogDefault`) via FmTopNav (desktop mega-menu) and
 * FmMobileNavDrawer (tablet/mobile accordion). The legacy hardcoded
 * TOPBAR_PRIMARY_NAV array and its VITE_FM_ROUTE_SHELL_TOP_PANEL cut-over flag
 * were deleted — no coexistence, no rollback flag. Every top-level owner job
 * also declares its activation contract: menu or direct route. The Assistant
 * is reached through the Ask control in this bar, shown only on routes that
 * serve one, so no shell control can open an unserved placeholder.
 *
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SearchOutlined, MenuOutlined, CloseOutlined, MessageOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useAssistantDock } from '@/contexts/AssistantDockContext';
import { FmButton } from '@/components/ui/FmButton';
import { usePropertyStore } from '@/stores/property';
import { usePropertySubscription } from '@/contexts/PropertySubscriptionContext';
import { userForestsAPI } from '@/api/userForests';
import { storyQueryKeys } from '@/queries/storyQueryKeys';
import { UserProfile, type UserProfileDestination } from '@/components/auth/UserProfile';
import LanguageSelector from '@/components/LanguageSelector';
import { AuthModal } from '@/components/auth/AuthModal';
import type { FmRouteCatalogData, FmRouteCatalogEntry } from '@/shared/ui/FmRouteCatalog';
import {
  getCatalogRouteFromPath,
  getCatalogRoutePropertyId,
  getRequiredCatalogRoute,
} from '@/shared/ui/FmRouteCatalog';
import { useLocalizedRouteCatalog } from '@/shared/ui/useLocalizedRouteCatalog';
import { useShellDocumentTitle } from '@/shared/ui/useShellDocumentTitle';
import FmTopNav from '@/shared/ui/FmTopNav';
import { FmMobileNavDrawer } from '@/shared/ui/FmNavComponents';
import { FmNavActiveProvider, useFmNavActive } from '@/shared/ui';
import {
  FmTopBarShellHeader,
  FmTopBarWordmark,
  FmTopBarHamburger,
} from '@/shared/ui/FmTopBarShell';
import {
  resolveCatalogDestination,
  resolveCatalogPropertySelectionDestination,
} from '@/shared/navigation/catalogNavigation';
import {
  parseLandingHandoffContext,
  SETTINGS_PACKAGES_SECTION,
  settingsSubscriptionPath,
} from '@/shared/navigation/landingHandoffContext';
import './FmTopBar.css';

interface CatalogMobileNavProps {
  open: boolean;
  onClose: () => void;
  catalog: FmRouteCatalogData;
  comingSoonLabel: string;
  resolveRoutePath: (route: FmRouteCatalogEntry) => string;
  resolveDirectJobPath: (route: FmRouteCatalogEntry) => string;
  onRouteClick: (routeId: string, path: string) => void;
  propertyLabel: string | null;
  propertyActionLabel: string;
  onPropertyActivate: () => void;
}

function requiredCatalogPath(catalog: FmRouteCatalogData, routeId: string): string {
  return getRequiredCatalogRoute(catalog, routeId).path;
}

function propertySwitchDestination(
  catalog: FmRouteCatalogData,
  pathname: string,
  returnTo: string,
): string {
  const currentRoute = getCatalogRouteFromPath(catalog, pathname);
  const targetRouteId = currentRoute?.propertySwitchRouteId ?? currentRoute?.id;
  const targetRoute = catalog.routes.find((route) => route.id === targetRouteId);
  return targetRoute?.placement === 'menu' && targetRoute.selection
    ? resolveCatalogPropertySelectionDestination(targetRoute, returnTo)
    : requiredCatalogPath(catalog, 'my-forests');
}

function chipPropertyId(
  catalog: FmRouteCatalogData,
  pathname: string,
  search: string,
  selectedProperty: { property_id: string } | null,
): string | null {
  const route = getCatalogRouteFromPath(catalog, pathname);
  const routePropertyId = getCatalogRoutePropertyId(catalog, pathname, search);
  // A path- or required-query forest is authoritative. An optional query
  // forest (campaign management, timber prices, Watch signals) narrows the
  // view when present and otherwise keeps the owner's selected forest, so the
  // chip and every property job stay on the forest the owner chose.
  const routeRequiresPropertyContext =
    route?.requiredContext === 'property' || route?.propertyContext?.requirement === 'required';
  return routeRequiresPropertyContext
    ? routePropertyId
    : (routePropertyId ?? selectedProperty?.property_id ?? null);
}

/** The property's name as the server records it, for the chip.
 *
 * Only a forest the owner is served is looked up: a link can name any parcel
 * (a buyer's diligence target, a mistyped id), and asking the property record
 * for one the owner cannot read only produces a 404. */
function useServedPropertyName(propertyId: string | null, enabled: boolean): string | null {
  const { getPropertySubscription } = usePropertySubscription();
  const served = useQuery({
    queryKey: storyQueryKeys.property.detail(propertyId ?? ''),
    enabled: enabled && Boolean(propertyId) && getPropertySubscription(propertyId ?? '') !== null,
    queryFn: () => userForestsAPI.getProperty(propertyId ?? ''),
    staleTime: 5 * 60 * 1000,
  });
  return served.data?.property_id === propertyId ? served.data.display_name : null;
}

function propertyPresentation(
  id: string | null,
  selectedProperty: { property_id: string; address?: string } | null,
  servedPropertyName: string | null,
): { id: string | null; label: string | null } {
  // A forest opened by link carries only its id in the store; the served
  // property record names it, so the chip reads the same on every page.
  const selectedLabel = selectedProperty?.property_id === id ? selectedProperty.address : null;
  const name = [selectedLabel, servedPropertyName]
    .map((candidate) => candidate?.trim())
    .find((candidate) => candidate && candidate !== id);
  const label = name ? `${name} · ${id}` : id;
  return { id, label };
}

const CatalogMobileNav: React.FC<CatalogMobileNavProps> = ({
  open,
  onClose,
  catalog,
  comingSoonLabel,
  resolveRoutePath,
  resolveDirectJobPath,
  onRouteClick,
  propertyLabel,
  propertyActionLabel,
  onPropertyActivate,
}) => {
  const { state } = useFmNavActive();
  return (
    <FmMobileNavDrawer
      open={open}
      onClose={onClose}
      catalog={catalog}
      topJobs={catalog.topJobs}
      resolveRoutePath={resolveRoutePath}
      resolveDirectJobPath={resolveDirectJobPath}
      onRouteClick={onRouteClick}
      propertyLabel={propertyLabel}
      propertyActionLabel={propertyActionLabel}
      onPropertyActivate={onPropertyActivate}
      activeRouteId={state.activeRouteId}
      activePrimaryNavId={state.activePrimaryNavId}
      comingSoonLabel={comingSoonLabel}
    />
  );
};

function catalogNavigationResolvers(propertyId: string | null, returnTo: string) {
  const route = (entry: FmRouteCatalogEntry): string =>
    resolveCatalogDestination(entry, {
      propertyId,
      returnTo,
    });
  const directJob = route;
  return { route, directJob };
}

function topBarPropertyContext(
  catalog: FmRouteCatalogData,
  location: { pathname: string; search: string; hash: string },
  property: { id: string | null; label: string | null },
) {
  const { pathname, search, hash } = location;
  const returnTo = `${pathname}${search}${hash}`;
  const propertySwitchPath = propertySwitchDestination(catalog, pathname, returnTo);
  return { property, returnTo, propertySwitchPath };
}

/** Subscription and pricing is the packages section of Settings, not a route. */
function accountDestination(
  activeRouteId: string | undefined,
  search: string,
): UserProfileDestination | null {
  if (activeRouteId === 'about') return 'about';
  if (activeRouteId !== 'settings') return null;
  return parseLandingHandoffContext(search).section === SETTINGS_PACKAGES_SECTION
    ? 'pricing'
    : 'settings';
}

/**
 * Account utilities (Settings, About) are catalog routes owned by the account
 * job, so the account control — not an owner-job tab — is the active
 * navigation element on those pages.
 */
function AccountMenu({
  catalog,
  search,
  onNavigate,
}: {
  catalog: FmRouteCatalogData;
  search: string;
  onNavigate: (path: string) => void;
}) {
  const { state } = useFmNavActive();
  const active = state.activePrimaryNavId === 'account';
  return (
    <div
      data-testid="account-menu"
      className={['fm-top-bar__account', active && 'fm-top-bar__account--active']
        .filter(Boolean)
        .join(' ')}
      data-active={active ? 'true' : undefined}
    >
      <UserProfile
        settingsLabel={getRequiredCatalogRoute(catalog, 'settings').label}
        aboutLabel={getRequiredCatalogRoute(catalog, 'about').label}
        currentDestination={active ? accountDestination(state.activeRouteId, search) : null}
        onViewSettings={() => onNavigate(requiredCatalogPath(catalog, 'settings'))}
        onViewPricing={() => onNavigate(settingsSubscriptionPath())}
        onViewAbout={() => onNavigate(requiredCatalogPath(catalog, 'about'))}
      />
    </div>
  );
}

/**
 * The Assistant's entry, in the bar rather than floating over the page: a
 * floating launcher sat on top of whatever reached the bottom-right corner (a
 * region action at desktop, the lede at phone width). Shown only while a route
 * serves an assistant; it names that route's question for assistive tech.
 */
const AssistantLauncher: React.FC = () => {
  const { launcherLabel, isOpen, toggle } = useAssistantDock();
  const { t } = useLanguage();
  if (!launcherLabel) return null;
  return (
    <FmButton
      variant={isOpen ? 'primary' : 'bordered'}
      className="fm-top-bar__ask"
      leadingIcon={<MessageOutlined />}
      data-testid="assistant-dock-trigger"
      aria-label={launcherLabel}
      aria-expanded={isOpen}
      title={launcherLabel}
      onClick={toggle}
    >
      <span className="fm-top-bar__ask-label">{t.shell.assistant.triggerLabel}</span>
    </FmButton>
  );
};

const FmTopBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const nav = t.shell.nav;
  const catalog = useLocalizedRouteCatalog();
  const selectedProperty = usePropertyStore((s) => s.selectedProperty);
  const propertyId = chipPropertyId(catalog, location.pathname, location.search, selectedProperty);
  const servedName = useServedPropertyName(propertyId, isAuthenticated);
  const { property, returnTo, propertySwitchPath } = topBarPropertyContext(
    catalog,
    location,
    propertyPresentation(propertyId, selectedProperty, servedName),
  );
  useShellDocumentTitle(
    getCatalogRouteFromPath(catalog, location.pathname)?.label ?? null,
    property.label,
  );
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);

  const destinations = catalogNavigationResolvers(property.id, returnTo);

  const handleCatalogRouteClick = (_routeId: string, path: string) => {
    navigate(path);
    setNavDrawerOpen(false);
  };

  return (
    <FmTopBarShellHeader
      testId="fm-top-bar"
      navAriaLabel={nav.primaryNavigation}
      left={
        <>
          <FmTopBarWordmark
            testId="fm-topbar-wordmark"
            onActivate={() => navigate(requiredCatalogPath(catalog, 'map'))}
          >
            Lehti AI
          </FmTopBarWordmark>
          {/* Hamburger — hidden at desktop, visible at tablet/mobile */}
          <FmTopBarHamburger
            testId="fm-topbar-hamburger"
            open={navDrawerOpen}
            ariaLabel={navDrawerOpen ? nav.closeNavigation : nav.openNavigation}
            onToggle={() => setNavDrawerOpen((v) => !v)}
          >
            {navDrawerOpen ? <CloseOutlined /> : <MenuOutlined />}
          </FmTopBarHamburger>
        </>
      }
      nav={
        <FmNavActiveProvider catalog={catalog} pathname={location.pathname}>
          <FmTopNav
            catalog={catalog}
            resolveRoutePath={destinations.route}
            resolveDirectJobPath={destinations.directJob}
            comingSoonLabel={nav.catalog.comingSoon}
            overflowLabel={nav.more}
            testId="fm-route-shell-top-nav"
          />
        </FmNavActiveProvider>
      }
      right={
        <>
          <AssistantLauncher />
          {!isAuthenticated && (
            <button
              type="button"
              className="fm-top-bar__login"
              data-testid="nav-login-btn"
              onClick={() => setAuthModalVisible(true)}
            >
              {nav.login}
            </button>
          )}
          <button
            type="button"
            className="fm-top-bar__search"
            data-testid="search-trigger-btn"
            aria-label={getRequiredCatalogRoute(catalog, 'browse-properties').label}
            onClick={() => navigate(requiredCatalogPath(catalog, 'browse-properties'))}
          >
            <SearchOutlined />
          </button>
          {property.id && (
            <button
              type="button"
              className="fm-top-bar__property-chip"
              data-testid="sidebar-property-footer"
              aria-label={`${nav.activeProperty}: ${property.label}. ${nav.changeProperty}`}
              onClick={() => navigate(propertySwitchPath)}
            >
              {property.label}
            </button>
          )}
          <span className="fm-top-bar__language-wrap">
            <LanguageSelector />
          </span>
          <FmNavActiveProvider catalog={catalog} pathname={location.pathname}>
            <AccountMenu
              catalog={catalog}
              search={location.search}
              onNavigate={(path) => navigate(path)}
            />
          </FmNavActiveProvider>
        </>
      }
    >
      {/* Nav drawer — catalog accordion overlay below top bar at tablet/mobile */}
      <FmNavActiveProvider catalog={catalog} pathname={location.pathname}>
        <CatalogMobileNav
          open={navDrawerOpen}
          onClose={() => setNavDrawerOpen(false)}
          catalog={catalog}
          comingSoonLabel={nav.catalog.comingSoon}
          resolveRoutePath={destinations.route}
          resolveDirectJobPath={destinations.directJob}
          onRouteClick={handleCatalogRouteClick}
          propertyLabel={property.label}
          propertyActionLabel={nav.changeProperty}
          onPropertyActivate={() => {
            navigate(propertySwitchPath);
            setNavDrawerOpen(false);
          }}
        />
      </FmNavActiveProvider>

      <AuthModal
        open={authModalVisible}
        onCancel={() => setAuthModalVisible(false)}
        onAuthenticated={() => {
          setAuthModalVisible(false);
          navigate(requiredCatalogPath(catalog, 'map'));
        }}
      />
    </FmTopBarShellHeader>
  );
};

export default FmTopBar;
